type InitializeGtmArgs = {
  gtmId: string;
  /** Tag Gateway URL ending with `/`, e.g. `https://lasertag.carbonify.de/gtm-nngl797/` */
  sourceUrl?: string;
  auth?: string;
  preview?: string;
  dataLayer?: Record<string, unknown>;
  dataLayerName?: string;
};

type WindowWithGatewayFetchPatch = Window & {
  __cbGtmGatewayCollectFetchPatched?: true;
};

const requestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
};

const fetchMode = (input: RequestInfo | URL, init?: RequestInit): RequestMode => {
  if (init?.mode) {
    return init.mode;
  }
  if (input instanceof Request) {
    return input.mode;
  }
  return 'cors';
};

/**
 * Chromium (incl. Samsung Internet) rejects `keepalive` fetches that need a CORS
 * preflight (`TypeError: Failed to fetch`). GTM CORS collect to lasertag uses
 * `keepalive: true` + `cache: "no-store"` (adds `Pragma`), so the request never
 * leaves the browser. Drop keepalive on CORS collect; lasertag CORS already
 * allows the preflight.
 *
 * Do not strip keepalive on `mode: "no-cors"` collect. That is gtag's unload
 * transport (`user_engagement` POST). Those requests do not preflight, and
 * without keepalive they abort when the page hides.
 */
const patchGatewayCollectFetch = (sourceUrl: string) => {
  const win = window as WindowWithGatewayFetchPatch;
  if (win.__cbGtmGatewayCollectFetchPatched || typeof win.fetch !== 'function') {
    return;
  }

  let gatewayOrigin: string;
  try {
    gatewayOrigin = new URL(sourceUrl).origin;
  } catch {
    return;
  }

  if (gatewayOrigin === win.location.origin) {
    return;
  }

  win.__cbGtmGatewayCollectFetchPatched = true;
  const nativeFetch = win.fetch.bind(win);

  win.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let url: string;
    try {
      url = new URL(requestUrl(input), win.location.href).href;
    } catch {
      return nativeFetch(input, init);
    }

    const isGatewayCollect = url.startsWith(`${gatewayOrigin}/`) && url.includes('/g/collect');
    if (!isGatewayCollect || fetchMode(input, init) === 'no-cors') {
      return nativeFetch(input, init);
    }

    if (input instanceof Request) {
      return nativeFetch(new Request(input, { ...init, keepalive: false }));
    }

    return nativeFetch(input, { ...init, keepalive: false });
  };
};

/**
 * Initializes GTM, optionally via Google Tag Gateway instead of googletagmanager.com.
 * When `sourceUrl` is set, Google-tag collect hits are also pointed at that host via
 * `server_container_url` so measurement does not use the page origin.
 * @see https://developers.google.com/tag-platform/tag-manager/server-side/send-data
 * @see https://developers.google.com/tag-platform/tag-manager/gateway/setup-guide?setup=manual&provider#google_tag_manager
 */
export const initializeGtm = ({
  gtmId,
  sourceUrl,
  auth = '',
  preview = '',
  dataLayer,
  dataLayerName = 'dataLayer',
}: InitializeGtmArgs) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (sourceUrl) {
    patchGatewayCollectFetch(sourceUrl);
  }

  const bootstrap: string[] = [];
  const initialDataLayer = {
    ...dataLayer,
    ...(sourceUrl ? { server_container_url: sourceUrl } : {}),
  };

  if (Object.keys(initialDataLayer).length > 0 || sourceUrl) {
    bootstrap.push(`window.${dataLayerName}=window.${dataLayerName}||[];`);
  }

  if (Object.keys(initialDataLayer).length > 0) {
    bootstrap.push(`window.${dataLayerName}.push(${JSON.stringify(initialDataLayer)});`);
  }

  if (sourceUrl) {
    bootstrap.push(
      `window.gtag=window.gtag||function(){window.${dataLayerName}.push(arguments);};`,
      `window.gtag('set',${JSON.stringify({ server_container_url: sourceUrl })});`,
    );
  }

  if (bootstrap.length > 0) {
    const dataScript = document.createElement('script');
    dataScript.innerHTML = bootstrap.join('');
    document.head.insertBefore(dataScript, document.head.childNodes[0]);
  }

  const gtmAuth = auth ? `&gtm_auth=${auth}` : '';
  const gtmPreview = preview ? `&gtm_preview=${preview}` : '';
  const gtmCookiesWin = sourceUrl ? '' : '&gtm_cookies_win=x';
  const scriptSrcBase = sourceUrl || 'https://www.googletagmanager.com/gtm.js';

  const script = document.createElement('script');
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'${scriptSrcBase}?id='+i+dl+'${gtmAuth}${gtmPreview}${gtmCookiesWin}';f.parentNode.insertBefore(j,f);
})(window,document,'script','${dataLayerName}','${gtmId}');`;
  document.head.insertBefore(script, document.head.childNodes[0]);

  // Noscript fallback stays on Google's domain (gateway docs only replace the JS snippet).
  if (!sourceUrl) {
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}${gtmAuth}${gtmPreview}&gtm_cookies_win=x" height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>`;
    document.body.insertBefore(noscript, document.body.childNodes[0]);
  }
};
